import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getMetrics, generateRealtimeMetric } from "@/api/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Play, Pause } from "lucide-react";

interface MetricDataPoint {
  time: string;
  value: string;
  isAnomaly?: boolean;
}

interface ServiceOption {
  value: string;
  label: string;
}

interface MetricOption {
  value: string;
  label: string;
}

const MetricsChart: React.FC = () => {
  const [data, setData] = useState<MetricDataPoint[]>([]);
  const [serviceName, setServiceName] = useState<string>("api-gateway");
  const [metricName, setMetricName] = useState<string>("avg_response_time");
  const [isLive, setIsLive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchMetrics();
  }, [serviceName, metricName]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (isLive) {
      interval = setInterval(() => {
        fetchRealtimeMetric();
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isLive, serviceName, metricName]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await getMetrics(serviceName, metricName, 50);
      if (response && response.length > 0) {
        const formattedData = response.map((item: any) => ({
          time: new Date(item.timestamp).toLocaleTimeString(),
          value: item.value.toFixed(2),
        }));
        setData(formattedData);
      } else {
        setData([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setData([]); // Set empty data instead of crashing
      setLoading(false);
    }
  };

  const fetchRealtimeMetric = async () => {
    try {
      const response = await generateRealtimeMetric(serviceName, metricName);
      if (response && response.metric && response.detection) {
        const newPoint = {
          time: new Date(response.metric.timestamp).toLocaleTimeString(),
          value: response.metric.value.toFixed(2),
          isAnomaly: response.detection.is_anomaly,
        };

        setData((prevData) => {
          const newData = [...prevData, newPoint];
          // Keep last 50 points
          return newData.slice(-50);
        });
      }
    } catch (error) {
      console.error("Error fetching realtime metric:", error);
      // Silently fail to prevent UI crash
    }
  };

  const services: ServiceOption[] = [
    { value: "api-gateway", label: "API Gateway" },
    { value: "graphql-service", label: "GraphQL Service" },
    { value: "user-service", label: "User Service" },
    { value: "order-service", label: "Order Service" },
    { value: "cache-service", label: "Cache Service" },
  ];

  const metrics: MetricOption[] = [
    { value: "avg_response_time", label: "Avg Response Time (ms)" },
    { value: "request_count", label: "Request Count" },
    { value: "error_rate", label: "Error Rate (%)" },
    { value: "cpu_usage", label: "CPU Usage (%)" },
    { value: "memory_usage", label: "Memory Usage (%)" },
  ];

  return (
    <div className="space-y-4" data-testid="metrics-chart">
      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={serviceName} onValueChange={setServiceName}>
          <SelectTrigger className="w-[200px]" data-testid="service-selector">
            <SelectValue placeholder="Select service" />
          </SelectTrigger>
          <SelectContent>
            {services.map((service) => (
              <SelectItem key={service.value} value={service.value}>
                {service.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={metricName} onValueChange={setMetricName}>
          <SelectTrigger className="w-[200px]" data-testid="metric-selector">
            <SelectValue placeholder="Select metric" />
          </SelectTrigger>
          <SelectContent>
            {metrics.map((metric) => (
              <SelectItem key={metric.value} value={metric.value}>
                {metric.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={isLive ? "destructive" : "default"}
          onClick={() => setIsLive(!isLive)}
          data-testid="live-toggle-btn"
        >
          {isLive ? (
            <>
              <Pause className="w-4 h-4 mr-2" />
              Stop Live
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Start Live
            </>
          )}
        </Button>

        {isLive && (
          <div className="flex items-center text-sm text-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2" />
            Live Monitoring
          </div>
        )}
      </div>

      {/* Chart */}
      {loading ? (
        <div className="h-64 flex items-center justify-center" data-testid="chart-loading">
          <p className="text-gray-500">Loading metrics...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 flex items-center justify-center" data-testid="no-metrics-data">
          <div className="text-center">
            <p className="text-gray-500">No metrics data available</p>
            <p className="text-sm text-gray-400 mt-1">Backend service may be unavailable</p>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={(props: any) => {
                const { cx, cy, payload } = props;
                if (payload.isAnomaly) {
                  return (
                    <circle cx={cx} cy={cy} r={6} fill="#ef4444" stroke="#dc2626" strokeWidth={2} />
                  );
                }
                return <circle cx={cx} cy={cy} r={3} fill="#3b82f6" />;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default MetricsChart;
